<template>
  <div class="detail-wage-board__layout common-wage-detail-board">
    <div class="detail-wage-board__row">
      <!-- 근무정보 -->
      <section class="wage-detail-card wage-detail-card--pay-hourly">
        <header class="wage-detail-card__head">
          <div class="wage-detail-card__head-text">
            <h3 class="wage-detail-card__title wage-detail-card__title--with-help">
              <span>근무정보</span>
              <HomeWageTooltip location="top" member-visible>
                <template #activator="{ props: tip }">
                  <v-icon v-bind="tip" size="14" class="wage-user-help-icon">mdi-help-circle-outline</v-icon>
                </template>
                <span style="white-space: pre-line">{{ workCalcHelpText }}</span>
              </HomeWageTooltip>
            </h3>
          </div>
          <div v-if="basicCalcModeAllowed" class="wage-detail-card__head-actions">
            <div class="wage-basic-calc-mode" role="group" aria-label="기본급 산출 방식">
              <button
                type="button"
                class="wage-basic-calc-mode__btn"
                :class="{ 'wage-basic-calc-mode__btn--active': basicCalcMode === 'hourly' }"
                :disabled="disabled"
                @click="emit('update:basicCalcMode', 'hourly')"
              >시급×시간</button>
              <button
                type="button"
                class="wage-basic-calc-mode__btn"
                :class="{ 'wage-basic-calc-mode__btn--active': basicCalcMode === 'basic' }"
                :disabled="disabled"
                @click="emit('update:basicCalcMode', 'basic')"
              >기본급→시간</button>
            </div>
          </div>
        </header>
        <div class="wage-detail-card__body wage-detail-card__body--stretch">
          <div class="wage-detail-card__content">
            <div class="wage-payroll-grid wage-payroll-grid--work">
              <HomeWageTooltip location="top">
                <template #activator="{ props: tooltipProps }">
                  <label
                    v-bind="tooltipProps"
                    class="wage-payroll-grid__label wage-payroll-grid__label--tooltip"
                    for="cw-hourly-wage"
                  >시급</label>
                </template>
                <span style="white-space: pre-line">시간당 임금 (기본: 최저시급){{ minHourlyWage ? `\n현재: ${formatWageNumber(parseFormMoney('cwhd_pay_calc_hourly'))}원` : '' }}</span>
              </HomeWageTooltip>
              <input
                id="cw-hourly-wage"
                :value="form.cwhd_pay_calc_hourly"
                type="text"
                class="wage-payroll-grid__input"
                placeholder="0"
                inputmode="numeric"
                :disabled="disabled"
                @input="emitAmountInput('cwhd_pay_calc_hourly', $event)"
                @blur="emit('hourly-blur')"
              />
              <HomeWageTooltip location="top">
                <template #activator="{ props: tooltipProps }">
                  <label
                    v-bind="tooltipProps"
                    class="wage-payroll-grid__label wage-payroll-grid__label--tooltip"
                    for="cw-work-hours"
                  >근무시간</label>
                </template>
                <span style="white-space: pre-line">{{ basicCalcMode === 'basic' ? '기본급 ÷ 시급으로 자동 산출됩니다.' : `월 총 근무 시간${form.cwhd_work_hours ? `\n현재: ${form.cwhd_work_hours}시간` : ''}` }}</span>
              </HomeWageTooltip>
              <input
                id="cw-work-hours"
                :value="form.cwhd_work_hours"
                type="text"
                class="wage-payroll-grid__input"
                :class="{ 'wage-payroll-grid__input--readonly': basicCalcMode === 'basic' }"
                inputmode="decimal"
                :disabled="disabled || basicCalcMode === 'basic'"
                @input="emitHoursInput('cwhd_work_hours', $event)"
                @blur="emitHoursBlur('cwhd_work_hours')"
              />
            </div>

            <div class="wage-payroll-grid wage-payroll-grid--hourly-linked">
              <label class="wage-payroll-grid__label" for="cw-work-days">근무일수</label>
              <input
                id="cw-work-days"
                :value="form.cwhd_work_days"
                type="text"
                class="wage-payroll-grid__input"
                inputmode="decimal"
                placeholder="근무일"
                :disabled="disabled"
                @input="emitHoursInput('cwhd_work_days', $event)"
                @blur="emitHoursBlur('cwhd_work_days')"
              />

              <template v-for="field in workInfoFields" :key="field.key">
                <HomeWageTooltip v-if="field.tooltip" location="top">
                  <template #activator="{ props: tooltipProps }">
                    <label
                      v-bind="tooltipProps"
                      class="wage-payroll-grid__label wage-payroll-grid__label--tooltip"
                      :for="fieldHoursId(field)"
                    >{{ field.label }}</label>
                  </template>
                  <span style="white-space: pre-line">{{ field.tooltip }}</span>
                </HomeWageTooltip>
                <label v-else class="wage-payroll-grid__label" :for="fieldHoursId(field)">{{ field.label }}</label>

                <template v-if="field.premiumHours && field.hoursKey">
                  <div class="wage-payroll-premium">
                    <input
                      :id="fieldHoursId(field)"
                      :value="form[field.hoursKey]"
                      type="text"
                      class="wage-payroll-premium__hours"
                      placeholder="시간"
                      inputmode="decimal"
                      :disabled="disabled"
                      @input="emitHoursInput(field.hoursKey!, $event)"
                      @blur="emitHoursBlur(field.hoursKey!)"
                    />
                    <span
                      class="wage-payroll-premium__amount wage-payroll-premium__amount--readonly"
                      :title="`${field.label} (시간×시급 자동계산)`"
                    >{{ formatCalcMoney(field.key) }}</span>
                  </div>
                </template>
                <template v-else-if="field.key === 'cwhd_basic'">
                  <input
                    v-if="basicCalcMode === 'basic'"
                    :id="`cw-${field.key}`"
                    type="text"
                    class="wage-payroll-grid__input"
                    :value="formatCalcMoney(field.key)"
                    inputmode="numeric"
                    :disabled="disabled"
                    @input="emitCalcAmountInput(field.key, $event)"
                    @blur="emitCalcAmountBlur(field.key, $event)"
                  />
                  <span
                    v-else
                    class="wage-payroll-grid__input wage-payroll-grid__input--readonly"
                    :title="field.tooltip"
                  >{{ formatCalcMoney(field.key) }}</span>
                </template>
                <span
                  v-else-if="field.readonly"
                  class="wage-payroll-grid__input wage-payroll-grid__input--readonly"
                >{{ formatCalcMoney(field.key) }}</span>
                <input
                  v-else
                  type="text"
                  class="wage-payroll-grid__input"
                  :value="formatCalcMoney(field.key)"
                  inputmode="numeric"
                  :disabled="disabled"
                  @input="emitCalcAmountInput(field.key, $event)"
                  @blur="emitCalcAmountBlur(field.key, $event)"
                />
              </template>
            </div>
          </div>
          <div class="wage-detail-card__footer">
            <div class="wage-card-total">
              <label class="wage-card-total__label">근무정보 합계</label>
              <span class="wage-card-total__value">{{ formatWageNumber(workInfoTotal) }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 비과세 -->
      <section class="wage-detail-card wage-detail-card--nonTax">
        <header class="wage-detail-card__head">
          <div class="wage-detail-card__head-text">
            <h3 class="wage-detail-card__title">비과세 수당</h3>
          </div>
        </header>
        <div class="wage-detail-card__body wage-detail-card__body--stretch">
          <div class="wage-detail-card__content">
            <div class="wage-payroll-grid">
              <template v-for="field in nonTaxInputFields" :key="field.key">
                <HomeWageTooltip v-if="field.tooltip" location="top">
                  <template #activator="{ props: tooltipProps }">
                    <label
                      v-bind="tooltipProps"
                      class="wage-payroll-grid__label wage-payroll-grid__label--tooltip"
                      :for="`cw-${field.key}`"
                    >{{ field.label }}</label>
                  </template>
                  <span style="white-space: pre-line">{{ field.tooltip }}</span>
                </HomeWageTooltip>
                <label v-else class="wage-payroll-grid__label" :for="`cw-${field.key}`">{{ field.label }}</label>
                <input
                  :id="`cw-${field.key}`"
                  :value="form[field.key as keyof CommonWageFormStrings]"
                  type="text"
                  class="wage-payroll-grid__input"
                  :class="{ 'wage-payroll-grid__input--error': field.key === 'cwhd_lunch' && lunchInvalid }"
                  inputmode="numeric"
                  :disabled="disabled"
                  @input="emitAmountInput(field.key as CcwdFormInputField, $event)"
                />
              </template>
            </div>

            <div
              v-if="showProductionNontax && hasPremiumNontax"
              class="wage-payroll-grid wage-payroll-grid--hourly-linked"
            >
              <template v-for="field in premiumNontaxFields" :key="field.key">
                <label class="wage-payroll-grid__label">{{ field.label }}</label>
                <span class="wage-payroll-grid__input wage-payroll-grid__input--readonly">{{ formatCalcMoney(field.key) }}</span>
              </template>
            </div>

            <div v-if="lunchWarning" class="wage-lunch-warning">{{ lunchWarning }}</div>

            <div class="wage-nontax-options">
              <HomeWageTooltip
                v-if="showProductionWorkerOption"
                location="top"
                text="연장·야간·휴일수당의 비과세 한도 월 20만원 적용 (생산직 근로자)"
              >
                <template #activator="{ props: tooltipProps }">
                  <label
                    v-bind="tooltipProps"
                    class="wage-nontax-options__item wage-nontax-options__item--tooltip"
                  >
                    <input
                      type="checkbox"
                      class="wage-nontax-options__checkbox"
                      :checked="productionNontax"
                      :disabled="disabled"
                      @change="onProductionNontaxChange"
                    />
                    <span class="wage-nontax-options__text">생산직 비과세</span>
                    <span class="wage-nontax-options__hint">(연야휴 월20만)</span>
                  </label>
                </template>
              </HomeWageTooltip>
              <HomeWageTooltip location="top" member-visible>
                <template #activator="{ props: tooltipProps }">
                  <label
                    v-bind="tooltipProps"
                    class="wage-nontax-options__item wage-nontax-options__item--tooltip"
                  >
                    <input
                      type="checkbox"
                      class="wage-nontax-options__checkbox"
                      :checked="lunchExpense"
                      :disabled="disabled"
                      @change="onLunchExpenseChange"
                    />
                    <span class="wage-nontax-options__text">중식비 실비변상</span>
                    <span class="wage-nontax-options__hint">(회계반영)</span>
                  </label>
                </template>
                <span style="white-space: pre-line">체크함: 기본급·수당·중식·과세 등 지급/과세 표시는 그대로 두고, 중식 한도 채움 후 과세 기준으로 4대보험·공제를 다시 계산합니다. 퇴직금은 총 지급금액에서 한도까지 채운 중식비를 빼고 12로 나눕니다.
체크 안 함: 입력한 중식비 기준으로 과세·공제를 계산합니다. 퇴직금은 총 지급금액 ÷ 12입니다.</span>
              </HomeWageTooltip>
            </div>
          </div>
          <div v-if="nonTaxTotal > 0 || showProductionNontax" class="wage-detail-card__footer">
            <div class="wage-card-total">
              <HomeWageTooltip location="top">
                <template #activator="{ props: tooltipProps }">
                  <label v-bind="tooltipProps" class="wage-card-total__label wage-card-total__label--tooltip">비과세액 합계</label>
                </template>
                <span style="white-space: pre-line">입력한 중식비 + 차량보조비 + 양육비{{ showProductionNontax ? ' + 연야휴 비과세' : '' }}
(중식비 실비변상 체크 시에도 한도 채움 금액이 아닌 입력 중식비 기준)</span>
              </HomeWageTooltip>
              <span class="wage-card-total__value">{{ formatWageNumber(nonTaxTotal) }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 과세 수당 -->
      <section class="wage-detail-card wage-detail-card--taxable-other">
        <header class="wage-detail-card__head">
          <div class="wage-detail-card__head-text">
            <h3 class="wage-detail-card__title">과세 수당</h3>
          </div>
        </header>
        <div class="wage-detail-card__body wage-detail-card__body--stretch">
          <div class="wage-detail-card__content">
            <div class="wage-payroll-grid">
              <template v-for="field in taxableOtherFields" :key="field.key">
                <label class="wage-payroll-grid__label" :for="`cw-${field.key}`">{{ field.label }}</label>
                <input
                  :id="`cw-${field.key}`"
                  :value="form[field.key as keyof CommonWageFormStrings]"
                  type="text"
                  class="wage-payroll-grid__input"
                  inputmode="numeric"
                  :disabled="disabled"
                  @input="emitAmountInput(field.key as CcwdFormInputField, $event)"
                />
              </template>
            </div>
          </div>
          <div class="wage-detail-card__footer">
            <div class="wage-card-total">
              <label class="wage-card-total__label">과세 수당 합계</label>
              <span class="wage-card-total__value">{{ formatWageNumber(taxableOtherTotal) }}</span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div class="detail-wage-board__row detail-wage-board__row--deduct">
      <!-- 4대보험 -->
      <section class="wage-detail-card wage-detail-card--socialInsurance">
        <header class="wage-detail-card__head">
          <div class="wage-detail-card__head-text">
            <h3 class="wage-detail-card__title wage-detail-card__title--with-help">
              <span>4대보험</span>
              <HomeWageTooltip location="top" member-visible>
                <template #activator="{ props: tip }">
                  <v-icon v-bind="tip" size="14" class="wage-user-help-icon">mdi-help-circle-outline</v-icon>
                </template>
                <span style="white-space: pre-line">{{ manualCheckboxHelp }}</span>
              </HomeWageTooltip>
            </h3>
          </div>
        </header>
        <div class="wage-detail-card__body wage-detail-card__body--stretch">
          <div class="wage-detail-card__content">
            <div class="wage-insurance-single">
              <div
                v-for="pair in insuranceShared"
                :key="pair.key"
                class="wage-insurance-single__row"
              >
                <HomeWageTooltip location="top">
                  <template #activator="{ props: tooltipProps }">
                    <span v-bind="tooltipProps" class="wage-insurance-single__name wage-insurance-single__name--tooltip wage-insurance-single__name-with-chip">
                      <span>{{ pair.label }}</span>
                      <span class="wage-insurance-single__role-chip wage-insurance-single__role-chip--placeholder" aria-hidden="true">근로</span>
                    </span>
                  </template>
                  <span style="white-space: pre-line">{{ pair.tooltip }}</span>
                </HomeWageTooltip>
                <HomeWageTooltip location="top" member-visible>
                  <template #activator="{ props: cbTip }">
                    <span v-bind="cbTip" class="wage-insurance-single__fix-cb-slot">
                      <input
                        type="checkbox"
                        class="wage-insurance-single__fix-cb wage-insurance-single__fix-cb--tooltip"
                        :checked="!!manualFlags[pair.key as keyof CommonWageManualFlags]"
                        :disabled="disabled"
                        @change="requestManualToggle(pair.key as keyof CommonWageManualFlags, $event)"
                      />
                    </span>
                  </template>
                  <span style="white-space: pre-line">{{ insuranceFixTooltip }}</span>
                </HomeWageTooltip>
                <input
                  type="text"
                  class="wage-payroll-grid__input"
                  :class="{ 'wage-payroll-grid__input--readonly': isManualLocked(pair.key) }"
                  :value="formatDeductOrCalc(pair.laborKey, pair.key as keyof CommonWageManualFlags)"
                  inputmode="numeric"
                  :disabled="disabled"
                  :readonly="isManualLocked(pair.key)"
                  @click.stop="onManualFieldActivate(pair.key, $event)"
                  @input="emit('deduct-input', pair.laborKey, $event)"
                  @blur="emit('deduct-blur', pair.laborKey, $event)"
                />
              </div>

              <div
                v-for="side in employmentSides"
                :key="`go-${side.role}`"
                class="wage-insurance-single__row"
              >
                <HomeWageTooltip location="top">
                  <template #activator="{ props: tooltipProps }">
                    <span v-bind="tooltipProps" class="wage-insurance-single__name wage-insurance-single__name--tooltip wage-insurance-single__name-with-chip">
                      <span>{{ employmentInsurance.label }}</span>
                      <span
                        class="wage-insurance-single__role-chip"
                        :class="side.role === 'labor' ? 'wage-insurance-single__role-chip--labor' : 'wage-insurance-single__role-chip--corp'"
                      >{{ side.chipLabel }}</span>
                    </span>
                  </template>
                  <span style="white-space: pre-line">{{ employmentInsurance.tooltip }}</span>
                </HomeWageTooltip>
                <HomeWageTooltip location="top" member-visible>
                  <template #activator="{ props: cbTip }">
                    <span v-bind="cbTip" class="wage-insurance-single__fix-cb-slot">
                      <input
                        type="checkbox"
                        class="wage-insurance-single__fix-cb wage-insurance-single__fix-cb--tooltip"
                        :checked="!!(manualFlags as Record<string, boolean>)[side.manualKey]"
                        :disabled="disabled"
                        @change="requestManualToggle(side.manualKey, $event)"
                      />
                    </span>
                  </template>
                  <span style="white-space: pre-line">{{ insuranceFixTooltip }}</span>
                </HomeWageTooltip>
                <input
                  type="text"
                  class="wage-payroll-grid__input"
                  :class="{ 'wage-payroll-grid__input--readonly': isManualLocked(side.manualKey) }"
                  :value="formatDeductOrCalc(side.key, side.manualKey)"
                  inputmode="numeric"
                  :disabled="disabled"
                  :readonly="isManualLocked(side.manualKey)"
                  @click.stop="onManualFieldActivate(side.manualKey, $event)"
                  @input="emit('deduct-input', side.key, $event)"
                  @blur="emit('deduct-blur', side.key, $event)"
                />
              </div>
            </div>
          </div>
          <div class="wage-detail-card__footer">
            <div class="wage-detail-card__footer-spacer" aria-hidden="true" />
          </div>
        </div>
      </section>

      <!-- 근로자공제 -->
      <section class="wage-detail-card wage-detail-card--laborDeduct">
        <header class="wage-detail-card__head">
          <div class="wage-detail-card__head-text">
            <h3 class="wage-detail-card__title wage-detail-card__title--with-help">
              <span>근로자공제</span>
              <HomeWageTooltip location="top" member-visible>
                <template #activator="{ props: tip }">
                  <v-icon v-bind="tip" size="14" class="wage-user-help-icon">mdi-help-circle-outline</v-icon>
                </template>
                <span style="white-space: pre-line">{{ manualCheckboxHelp }}</span>
              </HomeWageTooltip>
            </h3>
          </div>
        </header>
        <div class="wage-detail-card__body wage-detail-card__body--stretch">
          <div class="wage-detail-card__content">
            <div class="wage-insurance-single">
              <div
                v-for="field in laborOtherFields"
                :key="field.key"
                class="wage-insurance-single__row"
              >
                <span class="wage-insurance-single__name">{{ field.label }}</span>
                <span
                  v-if="field.manualOnly"
                  class="wage-insurance-single__fix-cb-slot wage-insurance-single__fix-cb-slot--empty"
                  aria-hidden="true"
                />
                <HomeWageTooltip v-else location="top" member-visible>
                  <template #activator="{ props: cbTip }">
                    <span v-bind="cbTip" class="wage-insurance-single__fix-cb-slot">
                      <input
                        type="checkbox"
                        class="wage-insurance-single__fix-cb wage-insurance-single__fix-cb--tooltip"
                        :checked="!!manualFlags[getManualFlagKey(field.key)]"
                        :disabled="disabled"
                        @change="requestManualToggle(getManualFlagKey(field.key), $event)"
                      />
                    </span>
                  </template>
                  <span style="white-space: pre-line">{{ insuranceFixTooltip }}</span>
                </HomeWageTooltip>
                <input
                  type="text"
                  class="wage-payroll-grid__input"
                  :class="{ 'wage-payroll-grid__input--readonly': !field.manualOnly && isManualLocked(getManualFlagKey(field.key)) }"
                  :value="field.manualOnly ? formatDeductMoney(field.key) : formatDeductOrCalc(field.key, getManualFlagKey(field.key))"
                  inputmode="numeric"
                  :disabled="disabled"
                  :readonly="!field.manualOnly && isManualLocked(getManualFlagKey(field.key))"
                  @click.stop="onLaborFieldActivate(field, $event)"
                  @input="emit('deduct-input', field.key, $event)"
                  @blur="emit('deduct-blur', field.key, $event)"
                />
              </div>
            </div>
          </div>
          <div class="wage-detail-card__footer">
            <div class="wage-card-total wage-card-total--grand">
              <label class="wage-card-total__label">근로자공제합계</label>
              <span class="wage-card-total__value">{{ formatCalcMoney('cwhd_l_total') }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 기관공제 -->
      <section class="wage-detail-card wage-detail-card--corpDeduct">
        <header class="wage-detail-card__head">
          <div class="wage-detail-card__head-text">
            <h3 class="wage-detail-card__title wage-detail-card__title--with-help">
              <span>기관공제</span>
              <HomeWageTooltip location="top" member-visible>
                <template #activator="{ props: tip }">
                  <v-icon v-bind="tip" size="14" class="wage-user-help-icon">mdi-help-circle-outline</v-icon>
                </template>
                <span style="white-space: pre-line">{{ manualCheckboxHelp }}</span>
              </HomeWageTooltip>
            </h3>
          </div>
        </header>
        <div class="wage-detail-card__body wage-detail-card__body--stretch">
          <div class="wage-detail-card__content">
            <div class="wage-insurance-single wage-insurance-single--corp-deduct">
              <div
                v-for="field in corpOtherFields"
                :key="field.key"
                class="wage-insurance-single__row"
              >
                <span class="wage-insurance-single__name">{{ field.label }}</span>
                <HomeWageTooltip location="top" member-visible>
                  <template #activator="{ props: cbTip }">
                    <span v-bind="cbTip" class="wage-insurance-single__fix-cb-slot">
                      <input
                        type="checkbox"
                        class="wage-insurance-single__fix-cb wage-insurance-single__fix-cb--tooltip"
                        :checked="!!manualFlags[getManualFlagKey(field.key)]"
                        :disabled="disabled"
                        @change="requestManualToggle(getManualFlagKey(field.key), $event)"
                      />
                    </span>
                  </template>
                  <span style="white-space: pre-line">{{ insuranceFixTooltip }}</span>
                </HomeWageTooltip>
                <input
                  type="text"
                  class="wage-payroll-grid__input"
                  :class="{ 'wage-payroll-grid__input--readonly': isManualLocked(getManualFlagKey(field.key)) }"
                  :value="formatDeductOrCalc(field.key, getManualFlagKey(field.key))"
                  inputmode="numeric"
                  :disabled="disabled"
                  :readonly="isManualLocked(getManualFlagKey(field.key))"
                  @click.stop="onManualFieldActivate(getManualFlagKey(field.key), $event)"
                  @input="emit('deduct-input', field.key, $event)"
                  @blur="emit('deduct-blur', field.key, $event)"
                />
              </div>
            </div>
            <div class="wage-representative-row">
              <HomeWageTooltip location="top">
                <template #activator="{ props: tooltipProps }">
                  <label
                    v-bind="tooltipProps"
                    class="wage-representative-row__label"
                  >
                    <input
                      type="checkbox"
                      class="wage-representative-row__cb"
                      :checked="isOwner"
                      :disabled="disabled"
                      @change="onIsOwnerChange"
                    />
                    <span>대표자</span>
                  </label>
                </template>
                <span style="white-space: pre-line">대표자: 고용·실업·산재보험, 퇴직금 미적용</span>
              </HomeWageTooltip>
            </div>
          </div>
          <div class="wage-detail-card__footer">
            <div class="wage-card-total wage-card-total--grand">
              <label class="wage-card-total__label">기관공제합계</label>
              <span class="wage-card-total__value">{{ formatCalcMoney('cwhd_c_total') }}</span>
            </div>
          </div>
        </div>
      </section>
    </div>

    <div class="wage-reference-bar-wrap">
      <p class="wage-reference-bar-hint">체크하면 금액 설정 창이 열립니다. 체크를 끄면 자동계산으로 돌아갑니다.</p>
      <section class="wage-reference-bar">
        <div class="wage-reference-bar__items">
        <div v-for="field in referenceFields" :key="field.key" class="wage-reference-bar__item">
          <HomeWageTooltip location="top">
            <template #activator="{ props: tooltipProps }">
              <span v-bind="tooltipProps" class="wage-reference-bar__label wage-reference-bar__label--tooltip">{{ field.label }}</span>
            </template>
            <span style="white-space: pre-line">{{ field.tooltip }}</span>
          </HomeWageTooltip>
          <span class="wage-reference-bar__value">{{ formatReferenceValue(field.key) }}원</span>
        </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import HomeWageTooltip from '@/components/wage/home/HomeWageTooltip.vue';
import { formatWageNumber, parseWageNumber, sanitizeHoursInput, parseHours } from '@/composables/wage/useWageCalculator';
import type { CommonWageCalcResult, CommonWageManualFlags } from '@/composables/wage/useCommonWageCalc';
import type { CommonWageFormStrings } from '@/utils/commonWageMapper';
import type { CcwdFormInputField } from '@/constants/commonWageFields';
import type { CommonWagePayrollFieldDef } from '@/policies/commonWagePayrollFields';
import {
  COMMON_WAGE_WORK_INFO_FIELDS,
  COMMON_WAGE_NON_TAX_INPUT_FIELDS,
  COMMON_WAGE_PREMIUM_NONTAX_FIELDS,
  COMMON_WAGE_TAXABLE_OTHER_FIELDS,
  COMMON_WAGE_INSURANCE_SHARED,
  COMMON_WAGE_EMPLOYMENT_INSURANCE,
  COMMON_WAGE_LABOR_OTHER_FIELDS,
  COMMON_WAGE_CORP_OTHER_FIELDS,
  COMMON_WAGE_REFERENCE_FIELDS,
  COMMON_WAGE_INSURANCE_FIX_TOOLTIP,
  COMMON_WAGE_USER_HELP_WORK_CALC,
  COMMON_WAGE_ADMIN_HELP_BASIC_CALC_MODE,
  COMMON_WAGE_USER_HELP_MANUAL_CHECKBOX,
} from '@/policies/commonWagePayrollFields';

const props = defineProps<{
  form: CommonWageFormStrings;
  displayCalc: CommonWageCalcResult;
  deductForm: Record<string, number>;
  manualFlags: CommonWageManualFlags;
  productionNontax: boolean;
  lunchExpense: boolean;
  showProductionWorkerOption: boolean;
  productionNontaxAllowed: boolean;
  minHourlyWage?: number;
  year?: number;
  disabled?: boolean;
  pensionExempt?: boolean;
  employmentExempt?: boolean;
  isOwner?: boolean;
  lunchInvalid?: boolean;
  lunchWarning?: string;
  basicCalcMode?: 'hourly' | 'basic';
  /** 기본급 산출 방식 토글 사용 가능 여부 (관리자 권한 80 이상) */
  basicCalcModeAllowed?: boolean;
}>();

const emit = defineEmits<{
  'amount-input': [field: CcwdFormInputField, value: string];
  'hours-input': [field: CcwdFormInputField, value: string];
  'hours-blur': [field: CcwdFormInputField];
  'hourly-blur': [];
  'toggle-manual': [field: keyof CommonWageManualFlags | string, checked: boolean];
  'open-manual': [field: keyof CommonWageManualFlags];
  'deduct-input': [key: string, event: Event];
  'deduct-blur': [key: string, event: Event];
  'calc-amount-input': [key: string, value: string];
  'calc-amount-blur': [key: string, event: Event];
  'update:productionNontax': [value: boolean];
  'update:lunchExpense': [value: boolean];
  'update:isOwner': [value: boolean];
  'update:basicCalcMode': [value: 'hourly' | 'basic'];
}>();

const workInfoFields = COMMON_WAGE_WORK_INFO_FIELDS;
const nonTaxInputFields = COMMON_WAGE_NON_TAX_INPUT_FIELDS;
const premiumNontaxFields = COMMON_WAGE_PREMIUM_NONTAX_FIELDS;
const taxableOtherFields = COMMON_WAGE_TAXABLE_OTHER_FIELDS;
const insuranceShared = COMMON_WAGE_INSURANCE_SHARED;
const employmentInsurance = COMMON_WAGE_EMPLOYMENT_INSURANCE;
const laborOtherFields = COMMON_WAGE_LABOR_OTHER_FIELDS;
const corpOtherFields = COMMON_WAGE_CORP_OTHER_FIELDS;
const referenceFields = COMMON_WAGE_REFERENCE_FIELDS;
const insuranceFixTooltip = COMMON_WAGE_INSURANCE_FIX_TOOLTIP;
const workCalcHelp = COMMON_WAGE_USER_HELP_WORK_CALC;
const adminBasicCalcHelp = COMMON_WAGE_ADMIN_HELP_BASIC_CALC_MODE;
const workCalcHelpText = computed(() =>
  basicCalcModeAllowed.value ? `${workCalcHelp}\n\n${adminBasicCalcHelp}` : workCalcHelp
);
const manualCheckboxHelp = COMMON_WAGE_USER_HELP_MANUAL_CHECKBOX;

const basicCalcMode = computed(() => props.basicCalcMode || 'hourly');
const basicCalcModeAllowed = computed(() => props.basicCalcModeAllowed === true);

const emitAmountInput = (field: CcwdFormInputField, event: Event) => {
  emit('amount-input', field, (event.target as HTMLInputElement).value);
};

const emitHoursInput = (field: CcwdFormInputField, event: Event) => {
  const target = event.target as HTMLInputElement;
  let cleaned: string;
  if (field === 'cwhd_work_days') {
    cleaned = target.value.replace(/[^\d]/g, '');
    const num = parseInt(cleaned, 10) || 0;
    if (num > 31) cleaned = '31';
  } else {
    cleaned = sanitizeHoursInput(target.value);
    const num = parseHours(cleaned);
    if (num >= 1000) cleaned = '999.9';
  }
  if (cleaned !== target.value) {
    target.value = cleaned;
  }
  emit('hours-input', field, cleaned);
};

const emitHoursBlur = (field: CcwdFormInputField) => {
  emit('hours-blur', field);
};

const emitCalcAmountInput = (key: string, event: Event) => {
  emit('calc-amount-input', key, (event.target as HTMLInputElement).value);
};

const emitCalcAmountBlur = (key: string, event: Event) => {
  emit('calc-amount-blur', key, event);
};

const requestManualToggle = (field: keyof CommonWageManualFlags, event: Event) => {
  if (props.disabled) return;
  const checked = (event.target as HTMLInputElement).checked;
  emit('toggle-manual', field, checked);
};

const isManualLocked = (field: keyof CommonWageManualFlags) => !!props.manualFlags[field];

const onManualFieldActivate = (field: keyof CommonWageManualFlags) => {
  if (props.disabled || !isManualLocked(field)) return;
  emit('open-manual', field);
};

const onLaborFieldActivate = (field: CommonWagePayrollFieldDef) => {
  if (field.manualOnly) return;
  onManualFieldActivate(getManualFlagKey(field.key));
};

const onProductionNontaxChange = (event: Event) => {
  if (props.disabled) return;
  const el = event.target as HTMLInputElement;
  const checked = el.checked;
  if (checked && !props.productionNontaxAllowed) {
    alert('생산직 비과세 적용 조건(직종·통상임금)을 충족하지 않습니다.');
    el.checked = false;
    return;
  }
  emit('update:productionNontax', checked);
};

const onLunchExpenseChange = (event: Event) => {
  if (props.disabled) return;
  emit('update:lunchExpense', (event.target as HTMLInputElement).checked);
};

const onIsOwnerChange = (event: Event) => {
  if (props.disabled) return;
  emit('update:isOwner', (event.target as HTMLInputElement).checked);
};

const showProductionNontax = computed(() => props.productionNontax);
const productionNontax = computed(() => props.productionNontax);
const lunchExpense = computed(() => props.lunchExpense);
const isOwner = computed(() => !!props.isOwner);
const hasPremiumNontax = computed(() =>
  premiumNontaxFields.some((f) => Number((props.displayCalc as Record<string, number>)[f.key]) > 0)
);

const parseFormMoney = (key: string) =>
  parseWageNumber(String(props.form[key as keyof CommonWageFormStrings] || ''));

const workInfoTotal = computed(() =>
  ['cwhd_basic', 'cwhd_overtime', 'cwhd_night', 'cwhd_holiday'].reduce(
    (sum, k) => sum + (Number((props.displayCalc as Record<string, number>)[k]) || 0),
    0
  )
);

/** 비과세액 합계 — 입력한 중식·차량·양육(+생산직 연야휴). 실비변상 채움(한도) 금액 미포함 */
const nonTaxTotal = computed(() => {
  let total = 0;
  for (const f of nonTaxInputFields) {
    total += parseFormMoney(f.key);
  }
  if (props.productionNontax) {
    total += premiumNontaxFields.reduce(
      (sum, f) => sum + (Number((props.displayCalc as Record<string, number>)[f.key]) || 0),
      0
    );
  }
  return total;
});

const taxableOtherTotal = computed(() =>
  taxableOtherFields.reduce((sum, f) => sum + parseFormMoney(f.key), 0)
);

const employmentSides = [
  { role: 'labor', chipLabel: '근로', key: COMMON_WAGE_EMPLOYMENT_INSURANCE.laborKey, manualKey: 'l_go' as const },
  { role: 'corp', chipLabel: '기관', key: COMMON_WAGE_EMPLOYMENT_INSURANCE.corpKey, manualKey: 'c_go' as const },
] as const;

function fieldHoursId(field: CommonWagePayrollFieldDef) {
  return field.hoursKey ? `cw-hours-${field.hoursKey}` : `cw-${field.key}`;
}

function formatCalcMoney(key: string) {
  return formatWageNumber(Number((props.displayCalc as Record<string, number>)[key]) || 0);
}

/** 공제 — 수동값 우선, 없으면 자동계산값 표시 (항상 입력 가능) */
function formatDeductOrCalc(laborKey: string, manualFlag: keyof CommonWageManualFlags) {
  if (props.manualFlags[manualFlag]) {
    return formatDeductMoney(laborKey);
  }
  return formatCalcMoney(laborKey);
}

function formatDeductMoney(key: string) {
  const val = props.deductForm[key];
  return val != null && val !== 0 ? formatWageNumber(val) : '';
}

function formatReferenceValue(key: string) {
  if (key === 'netPay') return formatCalcMoney('netPay');
  return formatCalcMoney(key);
}

function getManualFlagKey(deductKey: string): keyof CommonWageManualFlags {
  const map: Record<string, keyof CommonWageManualFlags> = {
    cwhd_l_so: 'l_so',
    cwhd_l_ji: 'l_ji',
    cwhd_l_jiwon: 'l_jiwon',
    cwhd_l_sik: 'l_sik',
    cwhd_l_etc: 'l_etc',
    cwhd_l_yearend: 'l_yearend',
    cwhd_c_sil: 'c_sil',
    cwhd_c_san: 'c_san',
    cwhd_c_out: 'c_out',
  };
  return map[deductKey] || 'l_etc';
}
</script>

<style lang="scss">
@import '@/assets/styles/variables';

.common-wage-detail-board {
  .wage-detail-card__title--with-help {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .wage-user-help-icon {
    color: #90a4ae;
    cursor: help;
    flex-shrink: 0;

    &:hover {
      color: #546e7a;
    }
  }

  .auto-calc-badge {
    font-size: 10px;
    font-weight: 500;
    color: #0369a1;
    background: #e0f2fe;
    padding: 2px 8px;
    border-radius: 4px;
    white-space: nowrap;
  }

  .wage-payroll-grid__input--readonly {
    cursor: pointer;
    color: #334155;
  }

  .wage-lunch-warning {
    margin-top: 6px;
    padding: 6px 8px;
    font-size: 10px;
    color: #c62828;
    background: #ffebee;
    border-radius: 4px;
  }

  .wage-payroll-grid__input--error {
    border-bottom-color: #c62828;
    background: rgba(#ffebee, 0.35);
  }
}
</style>
